/**
 * ============================================================================
 * ProductMessage Web Component
 * ============================================================================
 * A message field for the single product page (e.g. the wording written on a
 * cake). Whatever the shopper types is attached to the line item as a line item
 * property, so it travels to the cart, checkout and the order.
 *
 * WHY THIS COMPONENT IS NEEDED
 * ----------------------------
 * The theme's add to cart pipeline gathers line item properties in
 * getProductProperties() (assets/xo-webcomponents.js), run on every click of
 * xo-cart-add. That function only walks these shapes:
 *
 *   input[type=text|number|hidden][name^="properties["]
 *   input[type=radio|checkbox][name^="properties["]   (when checked)
 *   select[name^="properties["]
 *
 * A <textarea> is in none of them, so a textarea on its own would look right
 * in the browser and then silently never reach the cart. This component
 * therefore renders the textarea the shopper types into, and mirrors that value
 * into a hidden input named `properties[<property-name>]` - the exact shape the
 * pipeline already understands. The hidden input is also what makes the value
 * reach the cart, so the two are never allowed to drift apart.
 *
 * WHY NOT SIMPLY ASSOCIATE THE TEXTAREA WITH THE FORM
 * ---------------------------------------------------
 * The product page has two separate `form[action*="/cart/add"]` elements: the
 * main buy button form (id product_form_id-*) and the sticky add to cart bar
 * (id product-form-*). addCart serialises whichever form is closest to the
 * button that was clicked, so a textarea tied to only one of the two forms is
 * dropped half the time. The hidden input is collected by getProductProperties()
 * regardless of which form owns the click, so mirroring is what makes the
 * message work from both the inline and the sticky add to cart button.
 */
class ProductMessage extends HTMLElement {
    /**
     * ------------------------------------------------------------------------
     * STATIC CONFIG
     * ------------------------------------------------------------------------
     * The property name is the key the merchant sees in the Shopify admin under
     * the line item, so it is configurable per block. 'Message' matches the
     * naming the gift card recipient form already uses for its own message.
     *
     * Note for gift card products: the recipient form submits its own
     * `properties[Message]`. If this field is left on 'Message' too, the two
     * would compete and only the last one applied would survive. Set a
     * different property name for gift card products.
     */
    static DEFAULT_PROPERTY_NAME = 'Message';

    /**
     * ------------------------------------------------------------------------
     * DOM ELEMENT GETTERS
     * ------------------------------------------------------------------------
     */
    get textareaEl() {
        return this.querySelector('.product-message__textarea');
    }

    get hiddenEl() {
        return this.querySelector('input[type="hidden"]');
    }

    /**
     * ------------------------------------------------------------------------
     * ATTRIBUTE READERS
     * ------------------------------------------------------------------------
     * Configuration arrives as element attributes so the snippet can own the
     * markup and the component owns the behaviour.
     */
    get propertyName() {
        return this.getAttribute('property-name')?.trim() || ProductMessage.DEFAULT_PROPERTY_NAME;
    }

    /**
     * The name the add to cart pipeline expects. The pipeline matches on the
     * `properties[` prefix and posts the rest as the property key, so the
     * brackets belong to the name, not to the key.
     */
    get propertyFieldName() {
        return `properties[${this.propertyName}]`;
    }

    /**
     * ------------------------------------------------------------------------
     * DRAFT PERSISTENCE
     * ------------------------------------------------------------------------
     * The product form is re-rendered on every variant change (the theme swaps
     * the blocks in via xo-product-will-change), which would throw away
     * anything the shopper had already typed. Keeping the draft in
     * sessionStorage - per tab, gone when the tab closes - means the text
     * survives that re-render. The draft is intentionally NOT cleared on add to
     * cart, so re-adding the same product keeps the wording.
     */
    get storageKey() {
        return ProductMessage.STORAGE_PREFIX + this.productId;
    }

    get productId() {
        return this.closest('xo-product')?.getAttribute('xo-product-id') || 'unknown';
    }

    get savedDraft() {
        try {
            return sessionStorage.getItem(this.storageKey) || '';
        } catch (err) {
            // Storage unavailable: the draft simply is not remembered
            return '';
        }
    }

    set savedDraft(value) {
        try {
            if (value) {
                sessionStorage.setItem(this.storageKey, value);
            } else {
                sessionStorage.removeItem(this.storageKey);
            }
        } catch (err) {
            // Storage unavailable: the draft simply is not remembered
        }
    }

    /**
     * ------------------------------------------------------------------------
     * VALUE GETTERS & SETTERS
     * ------------------------------------------------------------------------
     * `message` is the trimmed shopper input. The cart property is written
     * empty (not omitted) when blank, because the pipeline skips empty values
     * on its own - an untouched field must not add a blank line to the item.
     */
    get message() {
        return this.textareaEl ? this.textareaEl.value.trim() : '';
    }

    set message(value) {
        if (this.textareaEl) {
            this.textareaEl.value = value;
        }
    }

    /**
     * ------------------------------------------------------------------------
     * MIRROR: TEXTAREA -> HIDDEN INPUT
     * ------------------------------------------------------------------------
     * The single place that writes the hidden input, so the transport value can
     * never lag behind the text the shopper can see.
     */
    syncHiddenInput() {
        if (!this.hiddenEl) return;

        this.hiddenEl.name = this.propertyFieldName;
        this.hiddenEl.value = this.message;
    }

    /**
     * ------------------------------------------------------------------------
     * EVENT HANDLERS
     * ------------------------------------------------------------------------
     */
    handleInput() {
        this.syncHiddenInput();
        this.savedDraft = this.textareaEl.value;
    }

    /**
     * ------------------------------------------------------------------------
     * LIFECYCLE HOOK: connectedCallback
     * ------------------------------------------------------------------------
     */
    connectedCallback() {
        // Step 1: Restore the draft left over from a previous variant re-render
        if (this.savedDraft) {
            this.message = this.savedDraft;
        }

        // Step 2: Publish the value the add to cart pipeline will read
        this.syncHiddenInput();

        // Step 3: Keep the hidden input in step while typing
        this.textareaEl?.addEventListener('input', () => {
            this.handleInput();
        });
    }

    /**
     * ------------------------------------------------------------------------
     * LIFECYCLE HOOK: disconnectedCallback
     * ------------------------------------------------------------------------
     * Persist on teardown as well: the product blocks can be swapped out by a
     * variant change at any moment, and the input event may not have fired for
     * the last keystrokes.
     */
    disconnectedCallback() {
        if (this.textareaEl) {
            this.savedDraft = this.textareaEl.value;
        }
    }
}

ProductMessage.STORAGE_PREFIX = 'product-message:';

// Register the custom element
if (!customElements.get('product-message')) {
    customElements.define('product-message', ProductMessage);
}
